import {
  cardSizeFromSettings,
  getPageDimensionsMm,
  mergePrintSettings,
} from './idCardPrintSettings';
import { wrapCardHtmlScaled } from './idCardScale';

const IN_TO_MM = 25.4;
const CROP_LEN_MM = 3;
const CROP_OFFSET_MM = 1.5;

/**
 * Compute grid dimensions and validation warnings.
 */
export function computeSheetGrid(settings) {
  const s = mergePrintSettings(settings);
  const { pageWidthMm, pageHeightMm } = getPageDimensionsMm(s);
  const cardWidthMm = s.cardWidthIn * IN_TO_MM;
  const cardHeightMm = s.cardHeightIn * IN_TO_MM;
  const printableW = pageWidthMm - 2 * s.marginMm;
  const printableH = pageHeightMm - 2 * s.marginMm;
  const warnings = [];

  const maxColsFit = Math.max(
    1,
    Math.floor((printableW + s.gapHorizontalMm) / (cardWidthMm + s.gapHorizontalMm)),
  );
  const maxRowsFit = Math.max(
    1,
    Math.floor((printableH + s.gapVerticalMm) / (cardHeightMm + s.gapVerticalMm)),
  );

  let cols;
  let rows;

  if (s.autoFit) {
    cols = maxColsFit;
    rows = maxRowsFit;
    if (s.includeBack && cols % 2 !== 0) {
      cols -= 1;
      if (cols < 2) cols = 2;
    }
  } else {
    cols = s.cardsPerRow || 2;
    rows = s.cardsPerColumn || 2;
  }

  const gridWidth = cols * cardWidthMm + (cols - 1) * s.gapHorizontalMm;
  const gridHeight = rows * cardHeightMm + (rows - 1) * s.gapVerticalMm;

  if (gridWidth > printableW + 0.01) {
    warnings.push(`Grid width (${gridWidth.toFixed(1)}mm) exceeds printable area (${printableW.toFixed(1)}mm).`);
  }
  if (gridHeight > printableH + 0.01) {
    warnings.push(`Grid height (${gridHeight.toFixed(1)}mm) exceeds printable area (${printableH.toFixed(1)}mm).`);
  }
  if (s.includeBack && cols % 2 !== 0) {
    warnings.push('Include back requires an even number of columns for front|back pairs.');
  }

  const slotsPerStudent = s.includeBack ? 2 : 1;
  const studentsPerRow = Math.floor(cols / slotsPerStudent);
  const studentsPerPage = Math.max(1, studentsPerRow * rows);

  return {
    cols,
    rows,
    cardWidthMm,
    cardHeightMm,
    pageWidthMm,
    pageHeightMm,
    printableW,
    printableH,
    gridWidth,
    gridHeight,
    studentsPerRow,
    studentsPerPage,
    slotsPerStudent,
    maxColsFit,
    maxRowsFit,
    warnings,
    valid: warnings.length === 0 && studentsPerRow > 0,
  };
}

function slotPosition(grid, col, row, settings) {
  const s = mergePrintSettings(settings);
  const xMm = s.marginMm + col * (grid.cardWidthMm + s.gapHorizontalMm);
  const yMm = s.marginMm + row * (grid.cardHeightMm + s.gapVerticalMm);
  return { xMm, yMm };
}

/**
 * Wrap card HTML to exact mm slot size — fit-to-slot scaling (no crop).
 */
export function wrapCardHtml(html, cardWidthMm, cardHeightMm, baseWidthPx, baseHeightPx) {
  return wrapCardHtmlScaled(html, cardWidthMm, cardHeightMm, baseWidthPx, baseHeightPx);
}

function cropMarksHtml(xMm, yMm, wMm, hMm) {
  const L = CROP_LEN_MM;
  const O = CROP_OFFSET_MM;
  const left = xMm - O;
  const top = yMm - O;
  const right = xMm + wMm + O;
  const bottom = yMm + hMm + O;

  return `
    <div class="crop-mark" style="left:${left}mm;top:${top}mm;width:${L}mm;height:0;border-top:0.25mm solid #999;"></div>
    <div class="crop-mark" style="left:${left}mm;top:${top}mm;width:0;height:${L}mm;border-left:0.25mm solid #999;"></div>
    <div class="crop-mark" style="left:${right - L}mm;top:${top}mm;width:${L}mm;height:0;border-top:0.25mm solid #999;"></div>
    <div class="crop-mark" style="left:${right}mm;top:${top}mm;width:0;height:${L}mm;border-left:0.25mm solid #999;"></div>
    <div class="crop-mark" style="left:${left}mm;top:${bottom}mm;width:${L}mm;height:0;border-top:0.25mm solid #999;"></div>
    <div class="crop-mark" style="left:${left}mm;top:${bottom - L}mm;width:0;height:${L}mm;border-left:0.25mm solid #999;"></div>
    <div class="crop-mark" style="left:${right - L}mm;top:${bottom}mm;width:${L}mm;height:0;border-top:0.25mm solid #999;"></div>
    <div class="crop-mark" style="left:${right}mm;top:${bottom - L}mm;width:0;height:${L}mm;border-left:0.25mm solid #999;"></div>
  `;
}

/**
 * Build paginated layout from student card HTML pairs.
 */
export function buildSheetLayout(pairs, settings) {
  const s = mergePrintSettings(settings);
  const grid = computeSheetGrid(s);
  const cardSize = cardSizeFromSettings(s);
  const pages = [];

  if (!pairs.length) {
    return { pages: [], grid, warnings: grid.warnings, cardSize };
  }

  const { studentsPerPage, studentsPerRow, slotsPerStudent } = grid;

  for (let pageStart = 0; pageStart < pairs.length; pageStart += studentsPerPage) {
    const pagePairs = pairs.slice(pageStart, pageStart + studentsPerPage);
    const slots = [];

    pagePairs.forEach((pair, studentIndex) => {
      const row = Math.floor(studentIndex / studentsPerRow);
      const colInStudents = studentIndex % studentsPerRow;

      const faces = s.includeBack
        ? [
            { html: pair.frontHtml, side: 'front', col: colInStudents * 2 },
            { html: pair.backHtml, side: 'back', col: colInStudents * 2 + 1 },
          ]
        : [{ html: pair.frontHtml, side: 'front', col: colInStudents }];

      faces.forEach(({ html, side, col }) => {
        const { xMm, yMm } = slotPosition(grid, col, row, s);
        slots.push({
          xMm,
          yMm,
          side,
          html: wrapCardHtml(
            html,
            grid.cardWidthMm,
            grid.cardHeightMm,
            cardSize.baseWidthPx,
            cardSize.baseHeightPx,
          ),
        });
      });
    });

    pages.push({ slots, pageIndex: pages.length });
  }

  return { pages, grid, warnings: grid.warnings, cardSize };
}

/**
 * Build full print-sheet HTML document (preview, print, PDF).
 */
export function buildPrintSheetHtml(pairs, settings) {
  const s = mergePrintSettings(settings);
  const { pages, grid, cardSize } = buildSheetLayout(pairs, s);
  if (!pages.length) return '';

  const { pageWidthMm, pageHeightMm } = grid;
  const pageBorderStyle = s.showPageBorder
    ? `.sheet-page::after { content:''; position:absolute; inset:${s.marginMm}mm; border:0.25mm solid #cbd5e1; pointer-events:none; }`
    : '';

  const pageHtml = pages.map(({ slots, pageIndex }) => {
    const slotEls = slots.map(({ xMm, yMm, html }, i) => {
      const crop = s.showCropMarks
        ? cropMarksHtml(xMm, yMm, grid.cardWidthMm, grid.cardHeightMm)
        : '';
      return `
        <div class="card-slot" style="left:${xMm}mm;top:${yMm}mm;width:${grid.cardWidthMm}mm;height:${grid.cardHeightMm}mm;">
          ${html}
        </div>
        ${crop}
      `;
    }).join('');

    return `<div class="sheet-page" data-page="${pageIndex + 1}">${slotEls}</div>`;
  }).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>ID Card Print Sheet</title><style>
    @page { size: ${pageWidthMm}mm ${pageHeightMm}mm; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { margin: 0; padding: 0; background: #fff; }
    .sheet-page {
      position: relative;
      width: ${pageWidthMm}mm;
      height: ${pageHeightMm}mm;
      page-break-after: always;
      overflow: hidden;
      background: #fff;
    }
    .sheet-page:last-child { page-break-after: auto; }
    .card-slot {
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: visible;
      background: #fff;
    }
    .card-scaled-inner { display: block; }
    .crop-mark { position: absolute; pointer-events: none; }
    ${pageBorderStyle}
  </style></head><body>${pageHtml}</body></html>`;
}

/** @deprecated Use buildPrintSheetHtml */
export function buildSheetPreviewHtml(pairs, settings = {}) {
  const merged = mergePrintSettings({
    sheetOrientation: settings.sheetOrientation || 'portrait',
    includeBack: settings.includeBack !== false,
    ...settings,
  });
  return buildPrintSheetHtml(pairs, merged);
}
